import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { filter, switchMap, take } from 'rxjs';
import { MedicineStatus } from 'src/app/medicine-user/enums/medicine-status.enum';
import { AddMedicineStockModalComponent } from 'src/app/medicine-user/modals/add-medicine-stock-modal/add-medicine-stock-modal.component';
import { UseMedicineStockModalComponent } from 'src/app/medicine-user/modals/use-medicine-stock-modal/use-medicine-stock-modal.component';
import { IMedicineResponse } from 'src/app/medicine-user/models/medicine.model';
import {
  IAddMedicineStockRequest,
  IUseMedicineRequest,
} from 'src/app/medicine-user/models/use-medicine.model';
import { MedicineApiService } from 'src/app/medicine-user/services/medicine-api.service';
import { AddToStock, UseMedicine } from 'src/app/medicine-user/state/medicine.actions';
import { AlertStatus } from 'src/app/risk-assessment-user/enums/alert-status.enum';
import { UiPillStatusEnum } from 'src/app/shared/enums/ui-pill-status.enum';

@Component({
  selector: 'app-medicine-template',
  templateUrl: './medicine-template.component.html',
  styleUrls: ['./medicine-template.component.scss'],
  standalone: false,
})
export class MedicineTemplateComponent {
  @Input() columnName: string;
  @Input() element: any;

  addToStockActions = [AddToStock];
  useActions = [UseMedicine];
  matchesRow = (action: AddToStock | UseMedicine): boolean =>
    action.medicine?.id === this.element?.id;

  private isUseDialogOpening = false;

  private statusMapper = {
    [MedicineStatus.Green]: UiPillStatusEnum.Ok,
    [MedicineStatus.Yellow]: UiPillStatusEnum.UnderThreshold,
    [MedicineStatus.Red]: UiPillStatusEnum.UnderMinimum,
  };

  private expiryStatusMapper = {
    [AlertStatus.Ok]: UiPillStatusEnum.Ok,
    [AlertStatus.Expiring]: UiPillStatusEnum.Expiring,
    [AlertStatus.Expired]: UiPillStatusEnum.Expired,
  };

  constructor(
    private dialog: MatDialog,
    private store: Store,
    private medicineApi: MedicineApiService
  ) {}

  get statusColor(): string {
    return this.statusMapper[this.element.stockStatus as MedicineStatus];
  }

  get expiryStatusColor(): string {
    return this.expiryStatusMapper[this.element.expiryStatus as AlertStatus];
  }

  use(): void {
    if (this.isUseDialogOpening) {
      return;
    }

    this.isUseDialogOpening = true;
    this.medicineApi
      .getMedicineById(this.element.id)
      .pipe(take(1))
      .subscribe({
        next: (medicine) => {
          this.isUseDialogOpening = false;
          this.openUseDialog(medicine);
        },
        error: () => {
          this.isUseDialogOpening = false;
        },
      });
  }

  addToStock(): void {
    this.dialog
      .open(AddMedicineStockModalComponent, {
        width: '45rem',
        panelClass: 'mobile-sizing-for-mat-dialogs',
        data: {
          id: `${this.element.id}`,
          currentStock: this.element.currentQuantity,
        },
      })
      .afterClosed()
      .subscribe((result: IAddMedicineStockRequest) => {
        if (result) {
          this.store.dispatch(new AddToStock(result, false));
        }
      });
  }

  private openUseDialog(medicine: IMedicineResponse): void {
    this.dialog
      .open(UseMedicineStockModalComponent, {
        width: '45rem',
        panelClass: 'mobile-sizing-for-mat-dialogs',
        data: {
          id: this.element.id,
          stockBatches: medicine.stockBatches ?? [],
        },
      })
      .afterClosed()
      .pipe(
        filter((result): result is IUseMedicineRequest => !!result),
        switchMap((result) => this.store.dispatch(new UseMedicine(result))),
        take(1)
      )
      .subscribe();
  }
}
